import axios from 'axios';
import { AsyncAction } from '..';
import { projectFactory } from '../../types/factory';
import { Project } from '../../types/project';

type State = {
    fetching: boolean;
    status: 'idle' | 'updating' | 'success' | 'fail';
    project?: Project;
    changed?: Project;
    changes: { date: Date; change: Partial<Project> }[];
    error?: Error;
};

type Action =
    | { type: 'project/reset' }
    | { type: 'project/status'; payload: { status: State['status'] } }
    | { type: 'project/set'; payload?: { project: Project }; error?: Error }
    | { type: 'project/edit'; payload: { change: Partial<Project> } }
    | { type: 'project/commit' }
    | { type: 'project/discard'; payload: { all: boolean } };

const initialState: State = {
    fetching: false,
    status: 'idle',
    changes: [],
};

/**
 * Apply `changes` to a `project` object returning a new object.
 *
 * @param project the base project
 * @param changes change to be applied
 * @returns updated object
 */
const withChanges = <T extends Project | Partial<Project>>(project?: T, changes?: Partial<Project>) =>
    ({ ...project, ...changes } as T);

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'project/reset': {
            return { ...initialState };
        }
        case 'project/status': {
            return { ...state, ...action.payload };
        }
        case 'project/set': {
            if (action.payload)
                return { ...state, fetching: false, ...action.payload, changed: action.payload.project, changes: [] };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        case 'project/edit': {
            if (!state.project) return { ...state, error: Error('cannot edit undefined project') };
            const changes = [...state.changes, { date: new Date(), ...action.payload }];
            const changed = changes.reduce((acc, c) => withChanges(acc, c.change), state.project);
            return { ...state, changes, changed };
        }
        case 'project/commit': {
            if (!state.project) return { ...state, error: Error('cannot commit undefined project') };
            return { ...state, status: 'idle', project: state.changed, changes: [] };
        }
        case 'project/discard': {
            if (!state.project) return { ...state, error: Error('cannot discard undefined project') };
            if (action.payload.all) state.changes = [];
            else state.changes.pop();
            const changed = state.changes.reduce((acc, c) => withChanges(acc, c.change), state.project);
            return { ...state, changed };
        }
        default:
            return state;
    }
};

/**
 * Reset the reducer to the initial state.
 */
const reset = (): Action => ({ type: 'project/reset' });

/**
 * Create a new project locally using the plan contained in the plan store.
 *
 * @param rethrow throw the internal error
 */
const create = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { plan } = getState();
    try {
        dispatch({ type: 'project/status', payload: { status: 'idle' } });
        dispatch({ type: 'project/set' });
        if (!plan.plan) throw new Error('plan store does not contain a plan');
        dispatch({ type: 'project/set', payload: { project: projectFactory(plan.plan.id) } });
    } catch (error) {
        dispatch({ type: 'project/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Get an existing project from the backend.
 *
 * @param id project id
 * @param rethrow throw the internal error
 */
const get = (id: number, rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'project/status', payload: { status: 'idle' } });
        dispatch({ type: 'project/set' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'project/set': cancel } });
        const response = await axios.get<Project>(`/inspections/${id}/`, { cancelToken: cancel.token });
        dispatch({ type: 'project/set', payload: { project: response.data } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'project/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Update the local project to the backend.
 * If the project does not exist in the backend, a new one is created, otherwise, it is updated.
 *
 * @param rethrow throw the internal error
 */
const update = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { project } = getState();
    try {
        dispatch({ type: 'project/status', payload: { status: 'updating' } });
        dispatch({ type: 'project/set' });
        if (!project.project) throw Error('project not available in redux store');
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'project/set': cancel } });
        const action = project.project.id === -1 ? axios.post : axios.put;
        const url = project.project.id === -1 ? '/inspections/' : `/inspections/${project.project.id}/`;
        const partialProject: Partial<Project> = { ...project.project };
        const response = await action<Project>(url, { ...partialProject }, { cancelToken: cancel.token });
        dispatch({ type: 'project/set', payload: { project: response.data } });
        dispatch({ type: 'project/status', payload: { status: 'success' } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'project/set', error });
        dispatch({ type: 'project/status', payload: { status: 'fail' } });
        if (rethrow) throw error;
    }
};

/**
 * Update the project information locally, but without committing it.
 *
 * @param change project object changes
 */
const edit = (change: Partial<Project>): Action => ({ type: 'project/edit', payload: { change } });

/**
 * Commit the edited project, the old project information cannot be restored.
 */
const commit = (): Action => ({ type: 'project/commit' });

/**
 * Discard the edited project.
 *
 * @param all if should discard all changes, not only the last
 */
const discard = (all = false): Action => ({ type: 'project/discard', payload: { all } });

export const creators = { reset, create, get, update, edit, commit, discard };
